(ns ductile.ui.components.popover
  (:require [ductile.ui.components :as ui]
            [nextjournal.clerk.render.hooks :as hooks]))

(defn- clamp [v min-v max-v]
  (-> v (max min-v) (min max-v)))

(defn- inside-event-target? [el ^js e]
  (and el (.contains el (.-target e))))

(defn- enabled-items [popover-el]
  (when popover-el
    (array-seq (.querySelectorAll popover-el "[data-popover-item=\"true\"]:not([disabled])"))))

(defn- focus-next-item! [popover-el direction]
  (when-let [items (seq (enabled-items popover-el))]
    (let [items (vec items)
          active js/document.activeElement
          current-index (or (.indexOf items active) -1)
          count-items (count items)
          next-index (if (neg? current-index)
                       ;; Start keyboard navigation on the first item regardless of arrow direction.
                       0
                       (mod (+ current-index direction count-items) count-items))]
      (when-let [el (nth items next-index nil)]
        (.focus el)))))

(defn- update-position! [trigger-el popover-el !pos]
  (when (and trigger-el popover-el)
    (let [^js trigger-rect (.getBoundingClientRect trigger-el)
          ^js popover-rect (.getBoundingClientRect popover-el)
          vw (.-innerWidth js/window)
          vh (.-innerHeight js/window)
          left (if (< (+ (.-left trigger-rect) (.-width popover-rect)) vw)
                 (.-left trigger-rect)
                 (- (.-right trigger-rect) (.-width popover-rect)))
          top (if (< (+ (.-bottom trigger-rect) (.-height popover-rect)) vh)
                (.-bottom trigger-rect)
                (- (.-top trigger-rect) (.-height popover-rect)))]
      (reset! !pos {:left (max 8 left)
                    :top (max 8 top)}))))

(defn- update-anchor-position! [trigger-el !pos]
  (when trigger-el
    (let [^js trigger-rect (.getBoundingClientRect trigger-el)]
      (reset! !pos {:left (max 8 (.-left trigger-rect))
                    :top (max 8 (.-bottom trigger-rect))}))))

(defn anchored-popover
  [{:keys [x y on-close data-testid class style est-width est-height]
    :or {x 0
         y 0
         est-width 320
         est-height 280}} & children]
  (let [!popover (hooks/use-ref)
        !armed? (hooks/use-ref false)
        !pos (hooks/use-state {:left (clamp x 8 (max 8 (- js/window.innerWidth est-width 8)))
                               :top (clamp y 8 (max 8 (- js/window.innerHeight est-height 8)))})]
    (hooks/use-effect
     (fn []
       (when-let [popover-el @!popover]
         (let [raf-id (atom nil)
               update-pos! (fn []
                             (let [^js rect (.getBoundingClientRect popover-el)
                                   left (clamp x 8 (max 8 (- js/window.innerWidth (.-width rect) 8)))
                                   top (clamp y 8 (max 8 (- js/window.innerHeight (.-height rect) 8)))]
                               (reset! !pos {:left left :top top})))
               close! (fn []
                        (when (fn? on-close)
                          (on-close)))
               on-global-pointerdown (fn [^js e]
                                       (when (and (.-current !armed?)
                                                  (not (inside-event-target? popover-el e)))
                                         (close!)))
               on-global-keydown (fn [^js e]
                                   (when-not (.-defaultPrevented e)
                                     (case (.-key e)
                                       ("ArrowDown" "ArrowUp")
                                       (do
                                         (.preventDefault e)
                                         (.stopPropagation e)
                                         (focus-next-item! popover-el (if (= "ArrowUp" (.-key e)) -1 1)))
                                       ("Enter" " " "Spacebar")
                                       (when (= "true" (.getAttribute js/document.activeElement "data-popover-item"))
                                         (.preventDefault e)
                                         (.stopPropagation e)
                                         (.click js/document.activeElement))
                                       "Escape"
                                       (do
                                         (.preventDefault e)
                                         (.stopPropagation e)
                                         (close!))
                                       nil)))]
           (set! (.-current !armed?) false)
           (reset! raf-id
                   (js/requestAnimationFrame
                    (fn []
                      (update-pos!)
                      (set! (.-current !armed?) true))))
           (.addEventListener js/window "resize" update-pos!)
           (.addEventListener js/window "scroll" update-pos! true)
           (.addEventListener js/window "keydown" on-global-keydown true)
           (.addEventListener js/document "pointerdown" on-global-pointerdown true)
           (fn []
             (when-let [id @raf-id]
               (js/cancelAnimationFrame id))
             (.removeEventListener js/window "resize" update-pos!)
             (.removeEventListener js/window "scroll" update-pos! true)
             (.removeEventListener js/window "keydown" on-global-keydown true)
             (.removeEventListener js/document "pointerdown" on-global-pointerdown true)))))
     [x y @!popover])
    (into
     [:div
      {:ref !popover
       :role "menu"
       :data-testid data-testid
       :class class
       :style (merge {:position "fixed"
                      :inset "auto"
                      :margin 0
                      :z-index (ui/z-index :combobox-choices)
                      :left (:left @!pos)
                      :top (:top @!pos)}
                     style)}]
     children)))

(defn menu-select
  [{:keys [data-testid class style text-class icon label items menu-class button-class button-content full-width? ref]
    :or {full-width? true}}]
  (let [!trigger (hooks/use-ref)
        !popover (hooks/use-ref)
        !open? (hooks/use-state false)
        !pos (hooks/use-state {:left 0 :top 0})
        !focus-direction-on-open (hooks/use-ref)
        popover-id (hooks/use-memo #(str "popover-" (random-uuid)) [])
        close! (fn [focus-trigger?]
                 (reset! !open? false)
                 (when focus-trigger?
                   (js/requestAnimationFrame
                    #(some-> @!trigger .focus))))
        open! (fn
                ([]
                 (set! (.-current !focus-direction-on-open) nil)
                 (update-anchor-position! @!trigger !pos)
                 (reset! !open? true))
                ([focus-direction]
                 (set! (.-current !focus-direction-on-open) focus-direction)
                 (update-anchor-position! @!trigger !pos)
                 (reset! !open? true)))
        on-item-click (fn [on-select]
                        (when (fn? on-select)
                          (on-select))
                        (close! true))]
    (hooks/use-effect
     (fn []
       (when (and @!open? @!popover)
         (let [popover-el @!popover
               raf-id (atom nil)
               update! #(update-position! @!trigger popover-el !pos)
               on-global-pointerdown (fn [^js e]
                                       (when (and (not (inside-event-target? popover-el e))
                                                  (not (inside-event-target? @!trigger e)))
                                         (close! true)))
               on-global-keydown (fn [^js e]
                                   (when (and (not (.-defaultPrevented e))
                                              (= "Escape" (.-key e)))
                                     (.preventDefault e)
                                     (.stopPropagation e)
                                     (close! true)))]
           (reset! raf-id
                   (js/requestAnimationFrame
                    (fn []
                      (update!)
                      (when-let [direction (.-current !focus-direction-on-open)]
                        (set! (.-current !focus-direction-on-open) nil)
                        (focus-next-item! popover-el direction)))))
           (.addEventListener js/window "resize" update!)
           (.addEventListener js/window "scroll" update! true)
           (.addEventListener js/window "keydown" on-global-keydown true)
           (.addEventListener js/document "pointerdown" on-global-pointerdown true)
           #(do
              (when-let [id @raf-id]
                (js/cancelAnimationFrame id))
              (.removeEventListener js/window "resize" update!)
              (.removeEventListener js/window "scroll" update! true)
              (.removeEventListener js/window "keydown" on-global-keydown true)
              (.removeEventListener js/document "pointerdown" on-global-pointerdown true)))))
     [@!open? @!trigger @!popover])

    (let [menu (when @!open?
                 [:div.min-w-44.max-h-96.overflow-auto
                  {:id popover-id
                   :ref !popover
                   :role "menu"
                   :aria-labelledby (str popover-id "-trigger")
                   :class (str "rounded-lg border border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.18)] "
                               menu-class)
                   :style {:position "fixed"
                           :inset "auto"
                           :margin 0
                           :z-index (ui/z-index :combobox-choices)
                           :left (:left @!pos)
                           :top (:top @!pos)}
                   :on-key-down (fn [^js e]
                                  (case (.-key e)
                                    "ArrowDown" (do (.preventDefault e) (focus-next-item! @!popover 1))
                                    "ArrowUp" (do (.preventDefault e) (focus-next-item! @!popover -1))
                                    ("Enter" " " "Spacebar")
                                    (when (= "true" (.getAttribute js/document.activeElement "data-popover-item"))
                                      (.preventDefault e)
                                      (.click js/document.activeElement))
                                    "Escape" (do (.preventDefault e) (close! true))
                                    nil))}
                  (into [:div {:class "flex flex-col items-stretch p-1 text-[12px]"}]
                        (map-indexed
                         (fn [idx {:keys [key label on-select disabled? separator? data-testid content]}]
                           (cond
                             separator?
                             ^{:key (str "separator-" idx)}
                             [:div.border-t.border-slate-200.my-1]
                             content
                             ^{:key (or key idx)}
                             [:div content]
                             :else
                             ^{:key (or key idx)}
                             [:button.text-left.px-2.py-1.5.rounded
                              {:type "button"
                               :role "menuitem"
                               :data-popover-item "true"
                               :data-testid data-testid
                               :disabled disabled?
                               :class (if disabled?
                                        "text-slate-400 cursor-not-allowed"
                                        "hover:bg-slate-100 focus:bg-slate-100 outline-none")
                               :on-click (fn [e]
                                           (.stopPropagation e)
                                           (on-item-click on-select))}
                              label]))
                         items))])]
      [:div.relative
       {:ref ref
        :class class
        :style style}
       [:button.appearance-none.bg-transparent.cursor-pointer.pr-2.focus:outline-none.focus:ring-0.focus-visible:outline-none.focus-visible:ring-0.text-left
        {:class (str (when full-width? "w-full ") button-class)
         :ref !trigger
         :id (str popover-id "-trigger")
         :data-testid data-testid
         :aria-haspopup "menu"
         :aria-expanded @!open?
         :aria-controls popover-id
         :on-key-down (fn [^js e]
                        (case (.-key e)
                          ("ArrowDown" "ArrowUp")
                          (do
                            (.preventDefault e)
                            (if @!open?
                              (when-let [popover-el @!popover]
                                (focus-next-item! popover-el (if (= "ArrowUp" (.-key e)) -1 1)))
                              (open! (if (= "ArrowUp" (.-key e)) -1 1))))
                          nil))
         :on-click (fn [_]
                     ;; Pre-position before opening to avoid a visible jump on first paint.
                     (if @!open?
                       (close! false)
                       (open!)))}
        (if button-content
          button-content
          [:<>
           (when icon
             [:span.pointer-events-none.absolute.inset-y-0.left-0.flex.items-center
              [ui/icon {:size 15} icon]])
           [:span {:class text-class} label]])]

       menu])))
